// Airway kit drawer: progression from simplest to most invasive.
import Drawer from './Drawer';

interface Props {
  open: boolean;
  onClose: () => void;
  currentAirway: string;
  onAction: (action_type: string, payload?: any) => void;
}

const ADJUNCTS = [
  { type: 'head_tilt_chin_lift', label: 'Head-tilt / chin-lift', notes: 'First action in non-trauma' },
  { type: 'jaw_thrust', label: 'Jaw thrust', notes: 'C-spine concern' },
  { type: 'insert_opa', label: 'OPA (Guedel)', notes: 'Size from incisors to angle of jaw. Unconscious only — risks gag if airway reflexes intact.', sets: 'opa' },
  { type: 'insert_npa', label: 'NPA', notes: 'Lubricated, 90° rotation. Avoid in basal skull #.', sets: 'npa' },
  { type: 'bvm_ventilation', label: 'BVM ventilation', notes: 'Two-person technique. 10 breaths/min during CPR (one every 6s).' },
  { type: 'attach_high_flow_o2', label: 'High-flow O₂', notes: 'NRB 15L/min while preparing definitive airway.' },
  { type: 'insert_igel', label: 'i-gel (supraglottic)', notes: 'Size 4 typical adult. No inflation. Confirm with capno.', sets: 'igel' },
  { type: 'insert_lma', label: 'LMA', notes: 'Inflate cuff to 60 cmH₂O.', sets: 'lma' },
  { type: 'intubate_attempt', label: 'Endotracheal intubation', notes: 'Tube size 7-8 adult. ≤30s attempt. Confirm with capnography.', sets: 'ett', warn: 'Definitive airway — but every attempt risks hypoxaemia. Abort at 30s.' },
  { type: 'confirm_ett_capno', label: 'Confirm ETT with capnography', notes: 'Sustained EtCO₂ waveform = correct placement.' },
  { type: 'suction', label: 'Suction airway', notes: 'Vomit / secretions. Yankauer for solids.' },
];

export default function AirwayKit({ open, onClose, currentAirway, onAction }: Props) {
  return (
    <Drawer open={open} onClose={onClose} title="Airway kit" width="md">
      <p className="text-xs text-mono-white/50 mb-3">
        Current airway: <span className="font-mono text-mono-cyan">{currentAirway}</span>
      </p>
      <div className="space-y-2">
        {ADJUNCTS.map((a) => {
          const isCurrent = a.sets && a.sets === currentAirway;
          return (
            <button
              key={a.type}
              onClick={() => { onAction(a.type); onClose(); }}
              className={`w-full text-left p-3 rounded transition-colors border ${
                isCurrent
                  ? 'bg-brand-600/20 border-brand-600 text-mono-white'
                  : 'bg-ink-800 hover:bg-ink-950 border-transparent hover:border-brand-600'
              }`}
            >
              <div className="flex justify-between items-baseline gap-2">
                <span className="font-medium">{a.label}</span>
                {isCurrent && <span className="text-[10px] text-brand-600 uppercase">In situ</span>}
              </div>
              <div className="text-xs text-mono-white/50 mt-1">{a.notes}</div>
              {a.warn && <div className="text-xs text-alarm-amber mt-1">⚠ {a.warn}</div>}
            </button>
          );
        })}
      </div>
    </Drawer>
  );
}
